import { useState, useEffect } from "react";
import { CalendarIcon, Search, MapPin, Users, Bed, Star, TrendingUp } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import { format } from "date-fns";
import { ar } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useCountries } from "@/hooks/useCountries";
import { Combobox } from "@/components/ui/combobox";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { TourismPackage } from "@/components/tourism-packages/types";
import { getStatusColor } from "@/components/tourism-packages/utils";
import { useTourismPackagesReal } from "@/hooks/useTourismPackagesReal";
import { usePackageFilters } from "@/hooks/usePackageFilters";

interface GuestConfiguration {
  adults: number;
  children: number;
  rooms: number;
}
const PackageSearch = () => {
  const navigate = useNavigate();
  const { data: countries } = useCountries();
  const { packages } = useTourismPackagesReal();
  const { filteredPackages } = usePackageFilters(packages || []);
  
  const [selectedCountry, setSelectedCountry] = useState("");
  const [checkInDate, setCheckInDate] = useState<Date>();
  const [checkOutDate, setCheckOutDate] = useState<Date>();
  const [loading, setLoading] = useState(false);
  
  const [guestConfig, setGuestConfig] = useState<GuestConfiguration>({
    adults: 1,
    children: 0,
    rooms: 1
  });
  const [isGuestPopoverOpen, setIsGuestPopoverOpen] = useState(false);

  // Get featured packages from real data
  const featuredPackages = filteredPackages
    .filter(p => p.status === 'active')
    .slice(0, 6);

  // Calculate popular destinations
  const popularDestinations = Object.entries(
    (packages || []).reduce((acc: any, pkg) => {
      acc[pkg.destination] = (acc[pkg.destination] || 0) + 1;
      return acc;
    }, {})
  )
    .map(([name, count]) => ({ name, count }))
    .sort((a: any, b: any) => b.count - a.count)
    .slice(0, 8);
  

  const countryOptions = countries?.map(country => ({
    label: country.name_ar,
    value: country.id
  })) || [];
  
  const incrementGuests = (type: keyof GuestConfiguration) => {
    setGuestConfig(prev => ({
      ...prev,
      [type]: prev[type] + 1
    }));
  };
  
  const decrementGuests = (type: keyof GuestConfiguration) => {
    setGuestConfig(prev => ({
      ...prev,
      [type]: Math.max(type === 'adults' ? 1 : 0, prev[type] - 1)
    }));
  };
  const handleSearch = () => {
    const searchParams = new URLSearchParams();
    if (selectedCountry) {
      const country = countries?.find(c => c.id === selectedCountry);
      searchParams.set('countryId', selectedCountry);
      if (country) searchParams.set('destination', country.name_ar);
    }
    if (checkInDate) searchParams.set('checkin', checkInDate.toISOString());
    if (checkOutDate) searchParams.set('checkout', checkOutDate.toISOString());
    searchParams.set('adults', guestConfig.adults.toString());
    searchParams.set('children', guestConfig.children.toString());
    searchParams.set('rooms', guestConfig.rooms.toString());
    navigate(`/tourism-packages?${searchParams.toString()}`);
  };
  const getGuestSummary = () => {
    const parts = [];
    if (guestConfig.adults) parts.push(`${guestConfig.adults} بالغ`);
    if (guestConfig.children) parts.push(`${guestConfig.children} طفل`);
    if (guestConfig.rooms > 1) parts.push(`${guestConfig.rooms} غرف`);else parts.push(`غرفة واحدة`);
    return parts.join(" • ");
  };
  return <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-secondary/5" dir="rtl">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-r from-primary to-primary-glow overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative container mx-auto px-4 py-[22px]">
          <div className="text-center text-white mb-12">
            <h1 className="text-4xl lg:text-6xl font-bold mb-4">بكجات جاهزة</h1>
            
          </div>

          {/* Search Form */}
          <Card className="max-w-4xl mx-auto shadow-2xl border-0">
            <CardContent className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                {/* Country Selection */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    الوجهة
                  </label>
                  <Combobox options={countryOptions} value={selectedCountry} onChange={setSelectedCountry} placeholder="اختر الوجهة" searchPlaceholder="ابحث عن وجهة..." emptyText="لا توجد وجهات مطابقة" className="w-full" />
                </div>

                {/* Check-in Date */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <CalendarIcon className="w-4 h-4" />
                    تاريخ الوصول
                  </label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className={cn("w-full justify-start text-right font-normal", !checkInDate && "text-muted-foreground")}>
                        <CalendarIcon className="ml-2 h-4 w-4" />
                        {checkInDate ? format(checkInDate, "PPP", {
                        locale: ar
                      }) : <span>اختر التاريخ</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar mode="single" selected={checkInDate} onSelect={setCheckInDate} disabled={date => date < new Date()} initialFocus className="pointer-events-auto" />
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Check-out Date */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <CalendarIcon className="w-4 h-4" />
                    تاريخ المغادرة
                  </label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className={cn("w-full justify-start text-right font-normal", !checkOutDate && "text-muted-foreground")}>
                        <CalendarIcon className="ml-2 h-4 w-4" />
                        {checkOutDate ? format(checkOutDate, "PPP", {
                        locale: ar
                      }) : <span>اختر التاريخ</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar mode="single" selected={checkOutDate} onSelect={setCheckOutDate} disabled={date => date < (checkInDate || new Date())} initialFocus className="pointer-events-auto" />
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Guests & Rooms */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    الضيوف والغرف
                  </label>
                  <Popover open={isGuestPopoverOpen} onOpenChange={setIsGuestPopoverOpen}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start text-right font-normal">
                        <Users className="ml-2 h-4 w-4" />
                        {getGuestSummary()}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-80 p-4" align="start">
                      <div className="space-y-4">
                        {/* Adults */}
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-medium">البالغون</div>
                            <div className="text-sm text-muted-foreground">فوق 12 سنة</div>
                          </div>
                          <div className="flex items-center gap-3">
                            <Button variant="outline" size="sm" onClick={() => decrementGuests('adults')} disabled={guestConfig.adults <= 1}>
                              -
                            </Button>
                            <span className="w-8 text-center">{guestConfig.adults}</span>
                            <Button variant="outline" size="sm" onClick={() => incrementGuests('adults')}>
                              +
                            </Button>
                          </div>
                        </div>

                        {/* Children */}
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-medium">الأطفال</div>
                            <div className="text-sm text-muted-foreground">تحت 12 سنة</div>
                          </div>
                          <div className="flex items-center gap-3">
                            <Button variant="outline" size="sm" onClick={() => decrementGuests('children')} disabled={guestConfig.children <= 0}>
                              -
                            </Button>
                            <span className="w-8 text-center">{guestConfig.children}</span>
                            <Button variant="outline" size="sm" onClick={() => incrementGuests('children')}>
                              +
                            </Button>
                          </div>
                        </div>

                        {/* Rooms */}
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-medium">الغرف</div>
                            <div className="text-sm text-muted-foreground">عدد الغرف المطلوبة</div>
                          </div>
                          <div className="flex items-center gap-3">
                            <Button variant="outline" size="sm" onClick={() => decrementGuests('rooms')} disabled={guestConfig.rooms <= 1}>
                              -
                            </Button>
                            <span className="w-8 text-center">{guestConfig.rooms}</span>
                            <Button variant="outline" size="sm" onClick={() => incrementGuests('rooms')}>
                              +
                            </Button>
                          </div>
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              {/* Search Button */}
              <div className="mt-6">
                <Button onClick={handleSearch} className="w-full lg:w-auto bg-primary hover:bg-primary/90 text-primary-foreground px-8 py-3 text-lg" size="lg">
                  <Search className="ml-2 h-5 w-5" />
                  ابحث عن الباقات
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Features Section */}
      
    </div>;
};
export default PackageSearch;